<template>
  <div class="chart-wrapper horizontal-bar-chart">
    <template v-if="loading">
      <h3 class="chart-title">{{ title }}</h3>
      <div class="horizontal-bar-chart__skeleton">
        <div
          v-for="n in 5"
          :key="n"
          class="horizontal-bar-chart__skeleton-row"
        />
      </div>
    </template>
    <template v-else-if="!hasData">
      <h3 class="chart-title">{{ title }}</h3>
      <bi-chart-empty />
    </template>
    <template v-else>
      <h3 class="chart-title">{{ title }}</h3>
      <div class="horizontal-bar-chart__content">
        <div class="horizontal-bar-chart__plot">
          <v-chart
            ref="chart"
            class="horizontal-bar-chart__chart"
            :style="{ height: `${chartHeight}px` }"
            :option="option"
            autoresize
            @click="onBarClick"
            @mouseover="onBarHover"
            @mouseout="onBarOut"
          />
        </div>
        <div class="horizontal-bar-chart__legend">
          <div
            v-for="(item, i) in data"
            :key="i"
            class="horizontal-bar-chart__legend-row"
            :class="{ 'horizontal-bar-chart__legend-row--active': activeIndex === i }"
            @mouseenter="onLegendEnter(i)"
            @mouseleave="onLegendLeave"
            @click="onLegendClick(i)"
          >
            <span
              class="horizontal-bar-chart__legend-dot"
              :style="{ background: colors[i] }"
            />
            <span class="horizontal-bar-chart__legend-name">
              {{ item.name }} {{ item.detail || `(${formatPercent(item.percent)}%)` }}
            </span>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import VChart from 'vue-echarts'
import { format } from 'echarts'
import BiChartEmpty from './BiChartEmpty.vue'
import { hasChartData } from './chartData'

const COLORS = ['#7095DD', '#F7A74E', '#C772A9', '#7FBFCC', '#B6B6B6']
const VALUE_LABEL_FONT = 'normal 14px sans-serif'
const VALUE_LABEL_DISTANCE = 5
const VALUE_LABEL_PADDING = 8

export default {
  name: 'HorizontalBarChart',
  components: { VChart, BiChartEmpty },
  props: {
    title: { type: String, default: '' },
    loading: { type: Boolean, default: false },
    data: {
      type: Array,
      default: () => [],
    },
    unit: { type: String, default: 'recoletas' },
    colors: { type: Array, default: () => COLORS },
    showRealValue: { type: Boolean, default: false },
  },
  data() {
    return {
      hoveredIndex: null,
      selectedIndex: null,
    }
  },
  computed: {
    hasData() {
      return hasChartData(this.data, 'value')
    },
    chartHeight() {
      const ROW_HEIGHT = 48
      return Math.max(200, this.data.length * ROW_HEIGHT)
    },
    activeIndex() {
      return this.hoveredIndex === null ? this.selectedIndex : this.hoveredIndex
    },
    option() {
      const maxVal = Math.max(0, ...this.data.map(d => d.value))
      const valueLabels = this.data.map(item => String(item.label || item.value))
      const labelWidth = Math.max(
        0,
        ...valueLabels.map(label => format.getTextRect(label, VALUE_LABEL_FONT).width)
      )
      const realValueLabels = this.showRealValue
        ? this.data.map(item => item.value?.toLocaleString('pt-BR') ?? '-')
        : []
      const realValueLabelWidth = Math.max(
        0,
        ...realValueLabels.map(label => format.getTextRect(label, VALUE_LABEL_FONT).width)
      )
      const series = [
        {
          type: 'bar',
          barWidth: 40,
          barCategoryGap: 8,
          data: this.data.map((d, i) => ({
            value: d.value,
            itemStyle: {
              color: this.colors[i] || COLORS[i % COLORS.length],
              borderRadius: 0,
            },
            emphasis: {
              itemStyle: {
                color: this.colors[i] || COLORS[i % COLORS.length],
                opacity: 1,
                shadowBlur: 0,
              },
            },
          })),
          emphasis: {},
          label: {
            show: true,
            position: 'left',
            distance: VALUE_LABEL_DISTANCE,
            formatter: params => valueLabels[params.dataIndex],
            fontSize: 14,
            fontFamily: 'sans-serif',
            fontWeight: 'normal',
            color: '#333',
          },
        },
      ]

      if (this.showRealValue) {
        series.push({
          type: 'scatter',
          data: this.data.map(d => d.value),
          symbolSize: 0,
          silent: true,
          tooltip: { show: false },
          label: {
            show: true,
            position: 'right',
            distance: VALUE_LABEL_DISTANCE,
            formatter: params => realValueLabels[params.dataIndex],
            fontSize: 14,
            fontFamily: 'sans-serif',
            fontWeight: 'normal',
            color: '#333',
          },
        })
      }

      return {
        tooltip: {
          trigger: 'axis',
          confine: true,
          axisPointer: { type: 'none' },
          formatter: params => {
            const d = this.data[params[0].dataIndex]
            const count = this.showRealValue
              ? ` · ${Number(d.value).toLocaleString('pt-BR')} exames`
              : ` · ${d.label || d.value} ${this.unit}`
            const quantity =
              !this.showRealValue && Number.isFinite(d.quantity)
                ? ` · ${d.quantity.toLocaleString('pt-BR')} exames`
                : ''
            return `<b>${d.name}</b> · ${this.formatPercent(d.percent)}%${count}${quantity}`
          },
        },
        grid: {
          top: 0,
          left: Math.ceil(labelWidth) + VALUE_LABEL_DISTANCE + VALUE_LABEL_PADDING,
          right: this.showRealValue
            ? Math.ceil(realValueLabelWidth) + VALUE_LABEL_DISTANCE + VALUE_LABEL_PADDING
            : VALUE_LABEL_PADDING,
          bottom: 0,
        },
        xAxis: {
          type: 'value',
          show: false,
          max: maxVal || 1,
        },
        yAxis: {
          type: 'category',
          data: this.data.map(d => d.name),
          inverse: true,
          axisLabel: { show: false },
          axisLine: { show: false },
          axisTick: { show: false },
        },
        series,
      }
    },
  },
  methods: {
    formatPercent(value) {
      return (value ?? 0).toFixed(2).replace('.', ',')
    },
    getChart() {
      const chart = this.$refs.chart
      if (!chart) return null
      if (typeof chart.dispatchAction === 'function') return chart
      if (typeof chart.getEchartsInstance === 'function')
        return chart.getEchartsInstance()
      return chart.chart || null
    },
    syncHighlight(index) {
      const chart = this.getChart()
      if (!chart) return

      chart.dispatchAction({ type: 'downplay', seriesIndex: 0 })

      if (index === null || index === undefined) {
        chart.dispatchAction({ type: 'hideTip' })
        return
      }

      chart.dispatchAction({ type: 'highlight', seriesIndex: 0, dataIndex: index })
      chart.dispatchAction({ type: 'showTip', seriesIndex: 0, dataIndex: index })
    },
    onLegendEnter(index) {
      this.hoveredIndex = index
      this.syncHighlight(index)
    },
    onLegendLeave() {
      this.hoveredIndex = null
      this.syncHighlight(this.selectedIndex)
    },
    onLegendClick(index) {
      this.selectedIndex = index
      this.syncHighlight(index)
      const d = this.data[index]
      this.$emit('bar-click', { name: d.name, value: d.value, percent: d.percent })
    },
    onBarHover(params) {
      if (params.componentType !== 'series') return
      this.hoveredIndex = params.dataIndex
    },
    onBarOut() {
      this.hoveredIndex = null
    },
    onBarClick(params) {
      this.selectedIndex = params.dataIndex
      this.syncHighlight(params.dataIndex)
      const d = this.data[params.dataIndex]
      this.$emit('bar-click', { name: d.name, value: d.value, percent: d.percent })
    },
  },
}
</script>

<style scoped lang="scss">
.chart-wrapper {
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
  flex: 1;
  box-sizing: border-box;
  font-family: 'Roboto', sans-serif;
}

.chart-title {
  font-size: 16px;
  font-weight: 700;
  color: rgba(70, 70, 70, 1);
  margin: 0 0 8px;
}

@keyframes bi-shimmer {
  0% {
    opacity: 0.4;
  }
  50% {
    opacity: 0.9;
  }
  100% {
    opacity: 0.4;
  }
}

.horizontal-bar-chart {
  &__skeleton {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 8px 0;

    &-row {
      height: 28px;
      border-radius: 2px;
      background: #e9e9e9;
      animation: bi-shimmer 1.4s ease-in-out infinite;

      &:nth-child(1) {
        width: 85%;
      }
      &:nth-child(2) {
        width: 65%;
      }
      &:nth-child(3) {
        width: 50%;
      }
      &:nth-child(4) {
        width: 35%;
      }
      &:nth-child(5) {
        width: 20%;
      }
    }
  }

  &__content {
    display: flex;
    gap: 10px;
    align-items: stretch;
  }

  &__plot {
    flex: 0 0 70%;
    width: 70%;
    min-width: 0;
  }

  &__chart {
    width: 100%;
    min-width: 0;
  }

  &__legend {
    flex: 0 0 30%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 0;
    min-width: 0;
  }

  &__legend-row {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #444;
    min-width: 0;
    height: 48px;
    padding: 0 8px 0 0;
    cursor: pointer;
    transition: background-color 120ms ease;
  }

  &__legend-dot {
    flex-shrink: 0;
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  &__legend-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
    color: rgba(22, 22, 22, 1);
  }
}
</style>
